/**
 * Notification Context
 * Wraps SaaSFoundation's notification functionality
 * Uses SaaSFoundation backend API via /api/v1/notifications
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { notificationService } from '../../services/notificationService';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch notifications from SaaSFoundation
  const fetchNotifications = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await notificationService.getNotifications(filters);
      
      console.log('[NotificationContext] Received response:', response);
      
      // Handle both response formats (service returns data directly from interceptor)
      if (response && response.notifications) {
        const notificationsArray = Array.isArray(response.notifications) ? response.notifications : [];
        console.log('[NotificationContext] Setting notifications:', notificationsArray.length);
        setNotifications(notificationsArray);
        
        // Update unread count from response if available
        if (response.unread_count !== undefined) {
          setUnreadCount(response.unread_count);
        } else {
          setUnreadCount(notificationsArray.filter(n => !n.read).length);
        }
      } else if (Array.isArray(response)) {
        console.log('[NotificationContext] Response is array, setting notifications:', response.length);
        setNotifications(response);
        setUnreadCount(response.filter(n => !n.read).length);
      } else {
        console.log('[NotificationContext] Empty response, using empty array');
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (err) {
      // Service interceptor should handle errors, but just in case
      console.error('[NotificationContext] Error fetching notifications:', err);
      // Even on error, try to get mock data
      try {
        const mockResponse = await notificationService.getNotifications(filters);
        if (mockResponse && mockResponse.notifications) {
          setNotifications(Array.isArray(mockResponse.notifications) ? mockResponse.notifications : []);
          setUnreadCount(mockResponse.unread_count || 0);
        } else {
          setNotifications([]);
          setUnreadCount(0);
        }
      } catch (mockErr) {
        console.error('[NotificationContext] Failed to get mock data:', mockErr);
        setNotifications([]);
        setUnreadCount(0);
      }
      setError(null); // Don't set error - service handles it gracefully
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh notifications (alias for fetchNotifications with current state)
  const refreshNotifications = useCallback(() => {
    fetchNotifications({ page: 1, page_size: 100 });
  }, [fetchNotifications]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationService.getUnreadCount();
      // Service interceptor returns { count: 0 } on error, so this is safe
      setUnreadCount(response?.count || 0);
    } catch (err) {
      // Service interceptor should handle errors, but just in case
      console.debug('Unread count fetch error (handled by service):', err);
      setUnreadCount(0);
    }
  }, []);

  // Initial load
  useEffect(() => {
    // Only fetch if we have a token (user is authenticated)
    // Check both token keys (auth_token is used by SaaSFoundation)
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (token) {
      // Fetch notifications and unread count
      // Errors are handled gracefully by the service (returns empty data for 401/403)
      fetchNotifications().catch(() => {
        // Silently handle - service already returns empty data
      });
      fetchUnreadCount().catch(() => {
        // Silently handle - service already returns empty data
      });
      
      // Refresh unread count periodically (only if token exists)
      const interval = setInterval(() => {
        const currentToken = localStorage.getItem('auth_token') || localStorage.getItem('token');
        if (currentToken) {
          fetchUnreadCount().catch(() => {
            // Silently handle - service already returns empty data
          });
        } else {
          // Token removed - stop polling and clear count
          setUnreadCount(0);
        }
      }, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    } else {
      // No token - set empty state
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [fetchNotifications, fetchUnreadCount]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      throw err;
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      throw err;
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      
      // Update local state
      const wasUnread = notifications.find(n => n.id === notificationId && !n.read);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to delete notification:', err);
      throw err;
    }
  }, [notifications]);

  // Delete multiple notifications
  const deleteMultipleNotifications = useCallback(async (notificationIds) => {
    try {
      await notificationService.deleteMultipleNotifications(notificationIds);
      
      // Update local state
      const deletedNotifications = notifications.filter(n => notificationIds.includes(n.id));
      const unreadDeleted = deletedNotifications.filter(n => !n.read).length;
      
      setNotifications(prev => prev.filter(n => !notificationIds.includes(n.id)));
      setUnreadCount(prev => Math.max(0, prev - unreadDeleted));
    } catch (err) {
      console.error('Failed to delete notifications:', err);
      throw err;
    }
  }, [notifications]);

  // Refresh notifications
  const refresh = useCallback(() => {
    fetchNotifications({ page: 1, page_size: 100 });
    fetchUnreadCount();
  }, [fetchNotifications, fetchUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteMultipleNotifications,
    refresh,
    fetchNotifications,
    refreshNotifications,
    fetchUnreadCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

